import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { DestinationEvent } from '@eventbrite/consumer-search.event-renderer';
import type { PaginatedEventsResponse } from '@/api/services/event-cards-service';
import { withBasePath } from '@/utils/withBasePath';
import { getPageSize } from '../_shared';
import { fetchOrganizerEventsFromDate } from '../_shared/fetchEventsFromDate';

export function useUpcomingEvents(
  organizerId: string,
  initialEvents: DestinationEvent[],
  initialHasMore: boolean,
  initialTotal: number,
  selectedDateFilter?: string | null,
  includeStarted = false,
  isExplicitDateFilter = true,
) {
  const pageSize = useMemo(() => getPageSize(), []);
  const hasSelectedDateFilter = Boolean(selectedDateFilter);
  const usesDateFilter = hasSelectedDateFilter && isExplicitDateFilter;
  const firstPage: PaginatedEventsResponse = {
    events: initialEvents,
    hasMore: initialHasMore,
    total: initialTotal,
  };

  const { data, isLoading, isError, fetchNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'organizer-upcoming-events',
      organizerId,
      pageSize,
      selectedDateFilter ?? 'all',
      includeStarted,
      usesDateFilter,
    ],
    queryFn: ({ pageParam = 1 }) =>
      usesDateFilter
        ? fetchOrganizerEventsFromDate(
            organizerId,
            selectedDateFilter as string,
            pageParam,
            pageSize,
            'start_asc',
            includeStarted,
          )
        : fetchOrganizerEventsPage(organizerId, pageParam, pageSize),
    getNextPageParam: (lastPage, allPages) => (lastPage.hasMore ? allPages.length + 1 : undefined),
    initialData: usesDateFilter ? undefined : { pages: [firstPage], pageParams: [1] },
    staleTime: hasSelectedDateFilter ? 0 : Infinity,
  });

  const events = data?.pages.flatMap((p) => p.events) ?? [];
  const lastPage = data?.pages?.[data.pages.length - 1];
  const hasMorePages = Boolean(lastPage?.hasMore);

  return {
    events,
    isLoading,
    isError,
    fetchNextPage,
    hasMorePages,
    isFetchingNextPage,
  };
}

async function fetchOrganizerEventsPage(
  organizerId: string,
  page: number,
  pageSize: number,
): Promise<PaginatedEventsResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  const res = await fetch(withBasePath(`/api/organizers/${organizerId}/events/?${params}`), {
    credentials: 'include',
  });
  if (!res.ok) {
    throw new Error(`Events request failed: ${res.status}`);
  }
  return (await res.json()) as PaginatedEventsResponse;
}
